import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  NbButtonModule,
  NbInputModule,
  NbOptionModule,
  NbSelectModule,
  NbSpinnerModule,
} from '@nebular/theme';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { KPI } from '../../../@core/api';
import { APIService } from '../../../@core/api/api.service';
import { AssetsService, InvestmentsService } from '../../../@core/utils';
import { AssetTypeSelectorPageComponent } from '../asset-type-selector/asset-type-selector-page.component';
import { AssetTypeWithHierarchy } from '../asset-type-selector/asset-type-selector.component';

@Component({
  selector: 'app-future-equipment-form',
  templateUrl: './future-equipment-form.component.html',
  styleUrls: ['./future-equipment-form.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    NbButtonModule,
    NbInputModule,
    NbSelectModule,
    NbOptionModule,
    NbSpinnerModule,
    FontAwesomeModule,
  ],
})
export class FutureEquipmentFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public nextAsset!: KPI.MappedAsset;
  @Input() public currentAsset!: KPI.MappedAsset;
  @Input() public readOnly: boolean = false;
  @Input() public investmentId: number | null = null;
  @Input() public buildingPerimeterId: number | null = null;
  @Input() public installationYear: number | null = null;
  @Output() public saved = new EventEmitter<KPI.Asset>();

  public baseLabel: string = '';
  public quantity: number | null = null;
  public zoneDetails: string = '';
  public comments: string = '';
  public selectedAssetType: AssetTypeWithHierarchy | null = null;
  public autoSelectAssetTypeId: number = 0;

  public showDetails: boolean = false;
  public saving: boolean = false;

  private initialLabel: string = '';
  private initialQuantity: number | null = null;
  private initialZoneDetails: string = '';
  private initialComments: string = '';
  private initialAssetTypeId: number = 0;

  private subscriptions: Subscription[] = [];
  private budgetOrigins: KPI.InvestmentBudgetOrigin[] = [];

  constructor(
    private assetsService: AssetsService,
    private cdRef: ChangeDetectorRef,
    private translate: TranslateService,
    private rightPanelDialogService: RightPanelDialogService,
    private api: APIService,
    private investmentsService: InvestmentsService,
  ) {}

  ngOnInit(): void {
    this.loadBudgetOrigins();
    if (this.nextAsset) {
      this.initializeFromNextAsset();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['nextAsset'] && !changes['nextAsset'].firstChange && this.nextAsset) {
      this.initializeFromNextAsset();
    }
  }

  private initializeFromNextAsset(): void {
    this.baseLabel = this.nextAsset.label || '';
    this.quantity = this.nextAsset.quantity || null;
    this.zoneDetails = (this.nextAsset as any).zone_details || '';
    this.comments = this.nextAsset.comments || '';
    this.autoSelectAssetTypeId = this.nextAsset.asset_type || 0;
    this.selectedAssetType = null;

    this.initialLabel = this.baseLabel;
    this.initialQuantity = this.quantity;
    this.initialZoneDetails = this.zoneDetails;
    this.initialComments = this.comments;
    this.initialAssetTypeId = this.autoSelectAssetTypeId;

    this.cdRef.markForCheck();
  }

  public get isDirty(): boolean {
    if (this.readOnly) return false;
    return (
      this.baseLabel !== this.initialLabel ||
      this.quantity !== this.initialQuantity ||
      this.zoneDetails !== this.initialZoneDetails ||
      this.comments !== this.initialComments ||
      (this.selectedAssetType !== null && this.selectedAssetType.id !== this.initialAssetTypeId)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private loadBudgetOrigins(): void {
    this.subscriptions.push(
      this.api.investments_investment_budget_origin().subscribe(origins => {
        this.budgetOrigins = origins;
      })
    );
  }

  public get displayLabel(): string {
    const base = this.baseLabel?.trim();
    const suffix = this.translate.instant('(future equipment)');
    if (!base) return suffix;
    return `${base} ${suffix}`;
  }

  public get currentAssetTypeName(): string {
    if (this.selectedAssetType) return this.selectedAssetType.name;
    return this.nextAsset?.asset_type_object?.name || '-';
  }

  public openAssetTypePanel(): void {
    if (this.readOnly) return;
    this.rightPanelDialogService.open(AssetTypeSelectorPageComponent, {
      autoFocus: false,
      closeOnBackdropClick: true,
      context: {
        autoSelectAssetTypeId: this.autoSelectAssetTypeId,
      },
    }).onClose.pipe(first()).subscribe((selected: AssetTypeWithHierarchy | null) => {
      if (selected) {
        this.selectedAssetType = selected;
        this.autoSelectAssetTypeId = selected.id;
        if (this.currentAsset && selected.id === this.currentAsset.asset_type) {
          this.baseLabel = this.currentAsset.label;
        } else {
          this.baseLabel = selected.name;
        }
        this.cdRef.markForCheck();

        // If asset type changes for future equipment linked to investment with pricesheet budget origin
        if (this.investmentId && this.initialAssetTypeId !== 0 && this.initialAssetTypeId !== selected.id) {
          this.handleAssetTypeChange();
        }
      }
    });
  }

  private handleAssetTypeChange(): void {
    this.subscriptions.push(
      this.investmentsService.getInvestment(this.investmentId!, false).pipe(
        first(),
        switchMap(investment => {
          const budgetOrigin = investment.budget_origin;
          // Check if budget origin is a pricesheet (price_list = true)
          if (budgetOrigin && (budgetOrigin as any).price_list) {
            // Find default budget origin
            const defaultOrigin = this.budgetOrigins.find(o => o.is_default);
            if (defaultOrigin) {
              // Remove pricesheet link and set budget origin to default
              return this.investmentsService.updateInvestment(this.investmentId!, {
                price_sheet: null,
                budget_origin: defaultOrigin.id
              } as any);
            }
          }
          return of(investment);
        })
      ).subscribe()
    );
  }

  public toggleDetails(): void {
    this.showDetails = !this.showDetails;
  }

  public isValid(): boolean {
    return !!(
      this.baseLabel?.trim() &&
      (this.selectedAssetType || this.nextAsset?.asset_type) &&
      this.quantity
    );
  }

  public save(): void {
    if (this.readOnly || this.saving || !this.isValid()) return;

    if (this.nextAsset?.id) {
      this.saveUpdate();
    } else if (this.investmentId) {
      this.saveCreate();
    }
  }

  private saveUpdate(): void {
    const payload: any = {};

    if (this.baseLabel.trim() !== this.initialLabel) {
      payload.label = this.baseLabel.trim();
    }
    if (this.quantity !== this.initialQuantity && this.quantity != null) {
      payload.quantity = this.quantity;
    }
    if (this.zoneDetails !== this.initialZoneDetails) {
      payload.zone_details = this.zoneDetails || '';
    }
    if (this.comments !== this.initialComments) {
      payload.comments = this.comments || '';
    }
    if (this.selectedAssetType && this.selectedAssetType.id !== this.initialAssetTypeId) {
      payload.asset_type = this.selectedAssetType.id;
    }

    if (Object.keys(payload).length === 0) return;

    this.saving = true;
    this.cdRef.markForCheck();

    this.subscriptions.push(
      this.assetsService.updateAsset(this.nextAsset.id, payload).subscribe({
        next: updatedAsset => {
          this.saving = false;
          this.saved.emit(updatedAsset);
          this.cdRef.markForCheck();
        },
        error: () => {
          this.saving = false;
          this.cdRef.markForCheck();
        },
      }),
    );
  }

  private saveCreate(): void {
    this.saving = true;
    this.cdRef.markForCheck();

    const buildingId = this.currentAsset?.perimeters?.find(p => !p.is_monosite)?.id
      ?? this.buildingPerimeterId;

    const createPayload = {
      label: this.baseLabel.trim(),
      quantity: this.quantity!,
      zone_details: this.zoneDetails || '',
      comments: this.comments || '',
      perimeter: buildingId!,
      asset_type: this.selectedAssetType!.id,
      installation_year: this.installationYear || 0,
      expert_mode: {},
      notes_pictures: {},
      maintenance_code: '',
      step: KPI.ASSET_STEP.FUTURE,
    };

    this.subscriptions.push(
      this.api.audit_asset_create(createPayload as any).pipe(
        switchMap((asset: KPI.Asset) =>
          this.api.investments_investments_patch(this.investmentId!, { next_asset: asset.id } as any).pipe(
            map(() => asset),
          ),
        ),
      ).subscribe({
        next: createdAsset => {
          this.saving = false;
          this.saved.emit(createdAsset);
          this.cdRef.markForCheck();
        },
        error: (err) => {
          console.error('[FutureEquipmentForm] saveCreate error:', err);
          this.saving = false;
          this.cdRef.markForCheck();
        },
      }),
    );
  }
}
